import { api } from './client';

export interface FuelEntry {
  id: string;
  legacyId: number | null;
  sum: number;
  createdAt: string;
}

export interface LimitHistoryEntry {
  month: string;
  amount: number;
}

export interface FuelResponse {
  monthlyLimit: number;
  remaining: number;
  approxLiters: number;
  lastPrice: number;
  entries: FuelEntry[];
  lastLimitDate: string | null;
  limitHistory: LimitHistoryEntry[];
}

export interface FuelArchiveMonth {
  month: string;
  entries: FuelEntry[];
  total: number;
}

export interface FuelArchiveResponse {
  months: FuelArchiveMonth[];
}

export interface SaveFuelDto {
  sum: number;
  id?: string;
}

export interface SaveFuelResponse {
  success: boolean;
  id: string;
  legacyId: number | null;
  sum?: number;
  action: 'created' | 'updated';
}

export interface DeleteFuelResponse {
  success: boolean;
  id: string;
  legacyId: number | null;
  action: 'deleted';
}

export interface FuelReceiptEvidence {
  id: string;
  receiptDate: string;
  receiptNumber: string | null;
  amount: number | null;
  liters: number | null;
  comment: string | null;
  matchedLoadId: string | null;
  matchedAt: string | null;
  hasPhoto: boolean;
  originalName: string | null;
  size: number;
  mimeType: string;
  width: number | null;
  height: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface FuelCardAssignment {
  id: string;
  legacyId: number | null;
  cardId: string | null;
  month: string;
  car: string;
  license: string;
  chip: string;
  consumptionPer100Km: number;
  isDiesel: boolean;
}

export interface FuelAssignmentConfirmation {
  id: string;
  month: string;
  assignmentId: string | null;
  confirmedAt: string;
  car: string;
  license: string;
  chip: string;
  consumptionPer100Km: number;
  isDiesel: boolean;
  isCurrentAssignment: boolean;
}

export interface FuelSelfAssignmentConfirmation {
  month: string;
  currentAssignment: FuelCardAssignment | null;
  confirmation: FuelAssignmentConfirmation | null;
}

export interface ConfirmSelfAssignmentDto {
  month: string;
  car?: string;
  license?: string;
  consumptionPer100Km?: number;
}

export interface SaveReceiptEvidenceDto {
  id?: string;
  receiptDate?: string;
  receiptNumber?: string;
  amount?: number;
  liters?: number;
  comment?: string;
}

export interface ReceiptEvidenceResponse {
  item: FuelReceiptEvidence;
}

function toReceiptFormData(dto: SaveReceiptEvidenceDto, photo?: File | Blob): FormData {
  const formData = new FormData();
  if (dto.receiptDate) formData.append('receiptDate', dto.receiptDate);
  if (dto.receiptNumber !== undefined) formData.append('receiptNumber', dto.receiptNumber);
  if (dto.amount !== undefined) formData.append('amount', String(dto.amount));
  if (dto.liters !== undefined) formData.append('liters', String(dto.liters));
  if (dto.comment !== undefined) formData.append('comment', dto.comment);
  if (photo) formData.append('photo', photo, photo instanceof File ? photo.name : 'receipt.jpg');
  return formData;
}

const multipartConfig = {
  headers: { 'Content-Type': 'multipart/form-data' },
};

export const fuelApi = {
  get: async (): Promise<FuelResponse> => {
    const { data } = await api.get('/fuel/me');
    return data;
  },

  getArchive: async (): Promise<FuelArchiveResponse> => {
    const { data } = await api.get('/fuel/me/archive');
    return data;
  },

  save: async (dto: SaveFuelDto): Promise<SaveFuelResponse> => {
    if (dto.id) {
      const { data } = await api.patch(`/fuel/manual-entries/${dto.id}`, { sum: dto.sum });
      return data;
    }
    const { data } = await api.post('/fuel/manual-entries', { sum: dto.sum });
    return data;
  },

  delete: async (id: string): Promise<DeleteFuelResponse> => {
    const { data } = await api.delete(`/fuel/manual-entries/${id}`);
    return data;
  },

  listReceiptEvidence: async (params: { month?: string } = {}): Promise<{ items: FuelReceiptEvidence[]; meta: { total: number } }> => {
    const { data } = await api.get('/fuel/receipt-evidence', { params });
    return data;
  },

  getSelfAssignmentConfirmation: async (
    params: { month?: string } = {},
  ): Promise<FuelSelfAssignmentConfirmation> => {
    const { data } = await api.get('/fuel/self/routes/assignment-confirmation', { params });
    return data;
  },

  confirmSelfAssignment: async (
    dto: ConfirmSelfAssignmentDto,
  ): Promise<FuelSelfAssignmentConfirmation> => {
    const { data } = await api.post('/fuel/self/routes/assignment-confirmation', dto);
    return data;
  },

  saveReceiptEvidence: async (
    dto: SaveReceiptEvidenceDto,
    photo?: File | Blob,
  ): Promise<ReceiptEvidenceResponse> => {
    if (dto.id) {
      const { data } = await api.patch(
        `/fuel/receipt-evidence/${dto.id}`,
        toReceiptFormData(dto, photo),
        multipartConfig,
      );
      return data;
    }
    const { data } = await api.post(
      '/fuel/receipt-evidence',
      toReceiptFormData(dto, photo),
      multipartConfig,
    );
    return data;
  },

  deleteReceiptEvidence: async (id: string): Promise<ReceiptEvidenceResponse> => {
    const { data } = await api.delete(`/fuel/receipt-evidence/${id}`);
    return data;
  },

  getReceiptPhotoBlobUrl: async (id: string, thumb = false): Promise<string> => {
    const response = await api.get(`/fuel/receipt-evidence/${id}/photo${thumb ? '?thumb=1' : ''}`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },
};
